import { buildViewLabel } from 'component/Offer/Service/__hooks/useViewLabel';
import { buildGuestRatingLabel } from 'component/Search/Filter/GuestRating/__helper/guestRatingLabel';
import { buildHotelCategoryFilterLabel } from 'component/Search/Filter/HotelCategory/__helper/hotelCategoryFilterLabel';
import { buildAttributeLabel } from 'component/Search/Filter/ScopedAttribute/__config/mapping';

import { buildRoomTypeLabel } from 'hooks/useRoomTypeLabel';

import {
    buildCategoryMinLogicMetaKey,
    getHotelCategoryMinLogicValue,
    isCategorySelected,
} from 'utility/Format/category';
import {
    buildMealTypeFilterLabel,
    buildMealTypeFilterTooltip,
    buildMealTypeMinLogicMetaKey,
    getMealTypeMinLogicValue,
    isMealTypeSelected,
} from 'utility/Format/mealType';
import { toggleValueInSet } from 'utility/Toggle/toggleValueInSet';

import {
    AccommodationFilter,
    Filter,
    HotelCategoryFilter,
} from 'types/Search/filter';
import { Search } from 'types/Search/form';
import {
    HotelCategoryPopularFilterValue,
    PopularFilterConfig,
    PopularFilterDefinition,
    PopularFilterInterface,
    PopularFilterValueType,
    StateProvider,
} from 'types/Search/popularFilter';

import { MealType } from 'proto/Offer/MealType';
import { AccommodationMeta, FilterInformation } from 'proto/Search/FilterMeta';
import { FilterKey } from 'proto/Search/PopularFilter';

import {
    buildDirectFlightFilterTooltip,
    buildRailAndFlyFilterTooltip,
    buildTransferFilterTooltip,
    buildWithNoBagFilterTooltip,
} from './tooltips';

type AccommodationKey = keyof Omit<
    AccommodationFilter,
    'rating' | 'withHygieneFacts'
>;
type AccommodationMetaKey = keyof Omit<
    AccommodationMeta,
    'rating' | 'withHygieneFacts'
>;
type AccommodationUpdateKey = keyof Omit<
    AccommodationFilter,
    'rating' | 'mealTypeList' | 'hotelCategoryList' | 'withHygieneFacts'
>;
type TransferFlightSectionKey = 'flight' | 'transfer';

const getAccommodationState = <Value extends string | number>(
    search: Search,
    key: AccommodationKey
) => search.filter.accommodation[key] as Set<Value>;

const mealTypeStateProvider: StateProvider<MealType> = (search, value) =>
    isMealTypeSelected(value, getAccommodationState(search, 'mealTypeList'));

const categoryStateProvider: StateProvider<HotelCategoryPopularFilterValue> = (
    search,
    value
) =>
    isCategorySelected(
        getCategoryFromString(value),
        getAccommodationState(search, 'hotelCategoryList')
    );

const accommodationStateProvider = <Value extends string | number>(
    search: Search,
    value: Value,
    key: AccommodationKey
) => getAccommodationState(search, key).has(value);

const accommodationMetaProvider = <Value extends string | number>(
    filterMeta: FilterInformation,
    value: Value,
    key: AccommodationMetaKey
) => filterMeta.accommodation?.[key]?.[value];

const accommodationStateUpdate = <Value extends string>(
    search: Search,
    value: Value,
    key: AccommodationUpdateKey
): Search => ({
    ...search,
    filter: {
        ...search.filter,
        accommodation: {
            ...search.filter.accommodation,
            [key]: toggleValueInSet(
                search.filter.accommodation[key] as Set<Value>,
                value
            ),
        },
    },
});

const ratingStateProvider = (search: Search, value: string) =>
    search.filter.accommodation.rating === Number(value);

const transferFlightStateProvider = <
    Section extends TransferFlightSectionKey,
    Key extends keyof Filter[Section]
>(
    search: Search,
    section: Section,
    key: Key
) => search.filter[section][key];

const transferFlightStateUpdate = <
    Section extends TransferFlightSectionKey,
    Key extends keyof Filter[Section]
>(
    search: Search,
    section: Section,
    key: Key
): Search => ({
    ...search,
    filter: {
        ...search.filter,
        [section]: {
            ...search.filter[section],
            [key]: !search.filter[section][key],
        },
    },
});

const getCategoryFromString = (category: HotelCategoryPopularFilterValue) =>
    Number(category) as HotelCategoryFilter;

const popularFilterConfig: PopularFilterConfig = {
    [FilterKey.MealType]: {
        labelProvider: (value, t, filterMeta) =>
            buildMealTypeFilterLabel(
                value,
                t,
                filterMeta.accommodation?.mealType
            ),
        tooltipProvider: buildMealTypeFilterTooltip,
        stateProvider: mealTypeStateProvider,
        metaProvider: (filterMeta, value) =>
            accommodationMetaProvider(
                filterMeta,
                buildMealTypeMinLogicMetaKey(value),
                'mealType'
            ),
        stateUpdate: (search, value) => ({
            ...search,
            filter: {
                ...search.filter,
                accommodation: {
                    ...search.filter.accommodation,
                    mealTypeList: mealTypeStateProvider(search, value)
                        ? new Set()
                        : getMealTypeMinLogicValue(value),
                },
            },
        }),
    },
    [FilterKey.RoomType]: {
        labelProvider: buildRoomTypeLabel,
        stateProvider: (search, value) =>
            accommodationStateProvider(search, value, 'roomTypeList'),
        metaProvider: (filterMeta, value) =>
            accommodationMetaProvider(filterMeta, value, 'roomType'),
        stateUpdate: (search, value) =>
            accommodationStateUpdate(search, value, 'roomTypeList'),
    },
    [FilterKey.OceanView]: {
        labelProvider: buildViewLabel,
        stateProvider: (search, value) =>
            accommodationStateProvider(search, value, 'viewTypeList'),
        metaProvider: (filterMeta, value) =>
            accommodationMetaProvider(filterMeta, value, 'viewType'),
        stateUpdate: (search, value) =>
            accommodationStateUpdate(search, value, 'viewTypeList'),
    },
    [FilterKey.HotelAttribute]: {
        labelProvider: buildAttributeLabel,
        stateProvider: (search, value) =>
            accommodationStateProvider(search, value, 'attributesList'),
        metaProvider: (filterMeta, value) =>
            accommodationMetaProvider(filterMeta, value, 'attributes'),
        stateUpdate: (search, value) =>
            accommodationStateUpdate(search, value, 'attributesList'),
    },
    [FilterKey.TourOperator]: {
        labelProvider: (value, _t, filterMeta) =>
            filterMeta.tourOperator?.items?.[value]?.label ?? '',
        stateProvider: (search, value) =>
            search.filter.tourOperatorList.has(value),
        metaProvider: (filterMeta, value) =>
            filterMeta.tourOperator?.items?.[value]?.meta,
        stateUpdate: (search, value) => ({
            ...search,
            filter: {
                ...search.filter,
                tourOperatorList: toggleValueInSet(
                    search.filter.tourOperatorList,
                    value
                ),
            },
        }),
    },
    [FilterKey.HotelCategory]: {
        labelProvider: buildHotelCategoryFilterLabel,
        stateProvider: categoryStateProvider,
        metaProvider: (filterMeta, value) =>
            accommodationMetaProvider(
                filterMeta,
                buildCategoryMinLogicMetaKey(getCategoryFromString(value)),
                'hotelCategory'
            ),
        stateUpdate: (search, value) => ({
            ...search,
            filter: {
                ...search.filter,
                accommodation: {
                    ...search.filter.accommodation,
                    hotelCategoryList: categoryStateProvider(search, value)
                        ? new Set()
                        : getHotelCategoryMinLogicValue(
                              getCategoryFromString(value)
                          ),
                },
            },
        }),
    },
    [FilterKey.Rating]: {
        labelProvider: buildGuestRatingLabel,
        stateProvider: (search, value) => ratingStateProvider(search, value),
        metaProvider: (filterMeta, value) =>
            filterMeta.accommodation?.rating?.[Number(value)],
        stateUpdate: (search, value) => ({
            ...search,
            filter: {
                ...search.filter,
                accommodation: {
                    ...search.filter.accommodation,
                    rating: ratingStateProvider(search, value)
                        ? undefined
                        : Number(value),
                },
            },
        }),
    },
    [FilterKey.Transfer]: {
        labelProvider: (_value, t) => t('TRANS-hotel-transfer-included'),
        tooltipProvider: (_value, t) => buildTransferFilterTooltip(t),
        stateProvider: (search) =>
            transferFlightStateProvider(search, 'transfer', 'hotelTransfer'),
        metaProvider: (filterMeta) => filterMeta.transfer?.hotelTransfer,
        stateUpdate: (search) =>
            transferFlightStateUpdate(search, 'transfer', 'hotelTransfer'),
    },
    [FilterKey.RailAndFly]: {
        labelProvider: (_value, t) => t('TRANS-rail-and-fly'),
        tooltipProvider: (_value, t) => buildRailAndFlyFilterTooltip(t),
        stateProvider: (search) =>
            transferFlightStateProvider(search, 'transfer', 'railAndFly'),
        metaProvider: (filterMeta) => filterMeta.transfer?.railAndFly,
        stateUpdate: (search) =>
            transferFlightStateUpdate(search, 'transfer', 'railAndFly'),
    },
    [FilterKey.DirectFlight]: {
        labelProvider: (_value, t) => t('TRANS-direct-flight'),
        tooltipProvider: (_value, t) => buildDirectFlightFilterTooltip(t),
        stateProvider: (search) =>
            transferFlightStateProvider(search, 'flight', 'directFlight'),
        metaProvider: (filterMeta) => filterMeta.transfer?.hotelTransfer,
        stateUpdate: (search) =>
            transferFlightStateUpdate(search, 'flight', 'directFlight'),
    },
    [FilterKey.WithNoBag]: {
        labelProvider: (_value, t) => t('TRANS-only-hand-luggage'),
        tooltipProvider: (_value, t) => buildWithNoBagFilterTooltip(t),
        stateProvider: (search) =>
            transferFlightStateProvider(search, 'flight', 'withNoBag'),
        metaProvider: (filterMeta) => filterMeta.transfer?.hotelTransfer,
        stateUpdate: (search) =>
            transferFlightStateUpdate(search, 'flight', 'withNoBag'),
    },
};

export const getPopularFilterConfig = <Key extends FilterKey>(
    popularFilter: PopularFilterInterface<Key>
): PopularFilterDefinition<PopularFilterValueType<Key>> =>
    popularFilterConfig[popularFilter.filterKey];

export const castPopularFilterValue = <Key extends FilterKey>(
    popularFilter: PopularFilterInterface<Key>
): PopularFilterValueType<Key> =>
    popularFilter.value as PopularFilterValueType<Key>;
